import React, { useState, useEffect } from "react";
import { Row, Col, Card, Typography, Button, message, Divider, Table, Input, Space, Tag, Select } from "antd";
import {
  SearchOutlined,
  ReadOutlined,
  RightCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { HeaderPage } from "../../../components/headerPage";
import { HeaderTitle } from "../../../components/headerTitle";
import { converterDecimalParaHorasMinutos } from "../../../helpers/formatDate";
import { useNavigate, useParams } from "react-router-dom";
import http from "../../../services/http";
import { maskMoney } from "../../../helpers/masks";

const { Text } = Typography;
const array_tipo = ["Curso Online (Autoestudo)", "Curso Interativo (Online ou Presencial)"];

// STATUS
const options_status = [
  { value: 2, label: "Em Andamento/Pendentes" },
  { value: 1, label: "Finalizado" },
  { value: 999, label: "Todos" },
];

export const LancamentoTreinamentos = () => {
  const { id_grupo } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState([]);

  // FILTRO
  const [search, setSearch] = useState("");
  const [filtroStatus, setFiltroStatus] = useState(2);

  // DADOS
  const [grupo, setGrupo] = useState({});

  useEffect(() => {
    if (id_grupo > 0) {
      carregarGrupo(id_grupo);
      listarCursos(id_grupo);
    }
  }, [id_grupo]);

  // CARREGAR GRUPO
  const carregarGrupo = async (id) => {
    if (!id) return;
    setLoading(true);
    const api = await http("get", `v1/treinamentos_grupos/carregar/${id}`, []);

    if (api.statusCode === 200) {
      setGrupo(api?.response?.data);
    } else {
      navigate("/treinamentos/lancamento/grupos");
      message.error(api.response.message);
    }
    setLoading(false);
  };

  // LISTAR
  const listarCursos = async (id) => {
    if (!id) return;
    setLoading(true);
    const api = await http("get", `v1/treinamentos_cursos/listar_ativos/${id}`, []);
    if (api.statusCode === 200) {
      setDados(api.response.data);
    } else {
      navigate("/treinamentos/lancamento/grupos");
      message.error(api.response.message);
    }
    setLoading(false);
  };

  // COLUNAS TABLE
  const columns = [
    {
      title: "Descrição",
      dataIndex: "descricao",
      sorter: (a, b) => a.descricao?.localeCompare(b.descricao),
    },
    {
      title: "Carga Horaria",
      dataIndex: "carga_horaria",
      render: (_, e) => converterDecimalParaHorasMinutos(e?.carga_horaria),
      sorter: (a, b) => maskMoney(a.carga_horaria)?.localeCompare(maskMoney(b.carga_horaria)),
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      render: (_, e) => array_tipo[e?.tipo - 1],
      sorter: (a, b) => array_tipo[a?.tipo - 1]?.localeCompare(array_tipo[b?.tipo - 1]),
    },
    {
      title: "Obrigatório",
      dataIndex: "obrigatorio",
      render: (_, e) => (e?.obrigatorio === 1 ? <Tag color="orange">Sim</Tag> : <Tag>Não</Tag>),
      sorter: (a, b) => (a?.obrigatorio === 1 ? "Sim" : "Não")?.localeCompare(b?.obrigatorio === 1 ? "Sim" : "Não"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, e) => {
        if (e?.status === 1) {
          return (
            <Tag icon={<CheckCircleOutlined />} color="green">
              Concluído
            </Tag>
          );
        } else if (e?.status === 2) {
          return (
            <Tag icon={<ClockCircleOutlined />} color="processing">
              Em andamento
            </Tag>
          );
        } else if (e?.status === 0) {
          return (
            <Tag icon={<CloseCircleOutlined />} color="error">
              Não Aprovado
            </Tag>
          );
        } else {
          return <Tag color="orange">Não iniciado</Tag>;
        }
      },
      sorter: (a, b) => (a.status === 1 ? "Ativo" : "Inativo")?.localeCompare(b.status === 1 ? "Ativo" : "Inativo"),
    },
    {
      title: "Ações",
      width: 50,
      render: (_, e) => {
        return (
          <Button
            loading={loading}
            onClick={() => navigate(`/treinamentos/lancamento/grupos/cursos/${id_grupo}/detalhes/${e?.id}`)}
            icon={<RightCircleOutlined />}
          />
        );
      },
      dataIndex: "acoes",
    },
  ];

  // PESQUISAR
  const handleFilter = (pesquisar, filtro) => {
    let filteredData = dados?.filter(
      (val) =>
        val.descricao?.toLowerCase().includes(pesquisar.toLowerCase()) ||
        maskMoney(val?.carga_horaria)?.toLowerCase().includes(pesquisar.toLowerCase()) ||
        array_tipo[val?.tipo - 1]?.toString()?.toLowerCase().includes(pesquisar.toLowerCase())
    );

    if (filtro === 1) {
      filteredData = filteredData.filter((e) => e?.status === 0 || e?.status === 1);
    } else if (filtro === 2) {
      filteredData = filteredData.filter((e) => e?.status === 2 || !e?.id_treinamentos_lancamento);
    }

    return filteredData;
  };

  const dadosFilter = dados?.length > 0 ? handleFilter(search, filtroStatus) : [];

  return (
    <Row align="middle" justify="center" className="content-row">
      <Col span={24} align="middle" justify="center">
        <Col xs={24} sm={24} md={22} lg={22} xl={20} xxl={16} align="left">
          <div className="content-item">
            <HeaderPage
              title="Treinamentos & Desenvolvimento"
              onClickBack={() => navigate("/treinamentos/lancamento/grupos")}
            />
            <HeaderTitle
              title={["Treinamentos & Desenvolvimento", "Grupos", "Treinamentos"]}
              subtitle={<Text italic>{grupo?.descricao ?? ""}</Text>}
              icon={<ReadOutlined />}
              loading={loading}
            />
            <Divider />
            <Card bordered={false} bodyStyle={{ padding: "3%" }}>
              <Space wrap>
                <Input
                  size="middle"
                  maxLength={100}
                  prefix={<SearchOutlined />}
                  className="input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Pesquisar..."
                  allowClear
                />

                <Select
                  style={{ minWidth: 180 }}
                  value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e)}
                  options={options_status}
                />
              </Space>

              <Divider />

              <Table
                rowKey="id"
                loading={loading}
                scroll={{ x: 700 }}
                columns={columns}
                dataSource={dadosFilter}
                size="small"
              />
            </Card>
          </div>
        </Col>
      </Col>
    </Row>
  );
};
